import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { TrendingUp, Users, MousePointerClick, DollarSign, Search, Copy, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

/**
 * AffiliateDashboard — public dashboard for individual affiliates.
 * Auth via `?token=<dashboard_magic_token>` (rotatable by admin).
 *
 * Sections:
 *   1. Header: affiliate name + window selector
 *   2. KPI cards: leads / first-clicks / conversion rate / revshare
 *   3. Leads table: no PII; lead_id, date, first-click flag, revshare.
 */
export default function AffiliateDashboard() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [days, setDays] = useState(30);
  const [dashboard, setDashboard] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");

  const loadAll = async (windowDays, query) => {
    if (!token) {
      setError("חסר קישור. פנה למנהל שלך.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const [dash, leadsRes] = await Promise.all([
        api.get(`/affiliate/dashboard?token=${encodeURIComponent(token)}&days=${windowDays}`),
        api.get(`/affiliate/dashboard/leads?token=${encodeURIComponent(token)}&days=${windowDays}${query ? `&q=${encodeURIComponent(query)}` : ""}`),
      ]);
      setDashboard(dash.data);
      setLeads(leadsRes.data.leads || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "שגיאה בטעינה — הקישור לא תקף או פג תוקף.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(days, ""); /* eslint-disable-next-line */ }, [days]);

  const copyLeadId = (leadId) => {
    navigator.clipboard.writeText(leadId);
    toast.success("Lead ID הועתק");
  };

  const exportCsv = () => {
    if (!leads.length) return;
    const header = ["lead_id", "signed_up_at", "utm_source", "utm_campaign", "first_offer_click_at", "first_offer_click_link_key", "revshare_earned"];
    const rows = leads.map((l) => header.map((h) => (l[h] ?? "")).join(","));
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredLeads = useMemo(() => {
    if (!q.trim()) return leads;
    return leads.filter((l) => (l.id || "").toLowerCase().includes(q.trim().toLowerCase()));
  }, [leads, q]);

  if (loading) {
    return (
      <div dir="rtl" className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-stone-500" data-testid="aff-dash-loading">טוען את הדשבורד...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div dir="rtl" className="min-h-screen bg-stone-50 flex items-center justify-center p-6">
        <div className="max-w-md bg-white rounded-2xl shadow-lg p-8 text-center border border-red-200" data-testid="aff-dash-error">
          <h2 className="font-[800] text-xl mb-2 text-red-800">נא לפנות למנהל</h2>
          <p className="text-sm text-stone-600 leading-[1.65]">{error}</p>
        </div>
      </div>
    );
  }

  const k = dashboard?.kpis || {};
  return (
    <div dir="rtl" className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-brand-amber">Affiliate Dashboard</div>
            <h1 className="text-2xl font-[800]" data-testid="aff-dash-name">שלום, {dashboard?.affiliate?.name}</h1>
            <div className="text-xs text-stone-500 mt-0.5 flex flex-wrap items-center gap-2">
              <span>List ID:</span>
              <code className="ltr bg-stone-100 rounded px-1.5 py-0.5">{dashboard?.affiliate?.list_id}</code>
              <span>·</span>
              <span>Revshare: <b>{dashboard?.affiliate?.revshare_percent}%</b></span>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-stone-100 rounded-full p-1" data-testid="aff-dash-window">
            {[7, 30, 90, 0].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                data-testid={`aff-dash-window-${d}`}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition ${
                  days === d ? "bg-white shadow-sm text-brand-charcoal" : "text-stone-500 hover:text-stone-700"
                }`}
              >
                {d === 0 ? "כל הזמן" : `${d} ימים`}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-6 space-y-5">
        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi label="לידים נרשמו" value={k.leads_in_window} hint={`${k.leads_all_time || 0} מאי פעם`} icon={Users} accent="blue" testId="kpi-leads" />
          <Kpi label="קליקים ראשונים" value={k.first_clicks_in_window} hint={`${k.first_clicks_all_time || 0} מאי פעם`} icon={MousePointerClick} accent="amber" testId="kpi-firstclicks" />
          <Kpi label="אחוז המרה" value={`${k.conversion_rate_pct || 0}%`} hint="קליקים ראשונים / לידים" icon={TrendingUp} accent="emerald" testId="kpi-conv" />
          <Kpi label="Revshare (חלון)" value={`$${(k.revshare_paid || 0) + (k.revshare_unpaid || 0)}`} hint={`שולם $${k.revshare_paid || 0} · תלוי $${k.revshare_unpaid || 0}`} icon={DollarSign} accent={((k.revshare_paid || 0) + (k.revshare_unpaid || 0)) > 0 ? "amber" : "stone"} testId="kpi-revshare" />
        </div>

        {/* Leads table */}
        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
          <div className="p-4 flex flex-wrap items-center justify-between gap-3 border-b border-stone-100">
            <div>
              <h2 className="font-[800] text-lg">הלידים שלך</h2>
              <p className="text-xs text-stone-500 mt-0.5">Lead ID לחיפוש חוזר במערכת שלך · ללא PII</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="w-4 h-4 text-stone-400 absolute right-3 top-1/2 -translate-y-1/2" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="חפש lead_id..."
                  className="h-10 ltr text-left pr-10 w-64"
                  data-testid="aff-dash-search"
                />
              </div>
              <Button
                onClick={exportCsv}
                disabled={!filteredLeads.length}
                variant="outline"
                className="rounded-full h-10 text-xs"
                data-testid="aff-dash-export"
              >
                ייצוא CSV
              </Button>
            </div>
          </div>
          {filteredLeads.length === 0 ? (
            <div className="p-12 text-center text-stone-500" data-testid="aff-dash-empty">
              {q ? "לא נמצאו לידים תואמים לחיפוש" : "עדיין אין לידים בחלון הזה"}
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="aff-dash-leads-table">
              <thead className="bg-stone-50 text-right">
                <tr>
                  <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">Lead ID</th>
                  <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">רישום</th>
                  <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">מקור (utm_source)</th>
                  <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">קליק ראשון</th>
                  <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">הצעה</th>
                  <th className="px-4 py-3 font-bold text-xs uppercase tracking-wider text-stone-500">Revshare</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map((l) => (
                  <tr key={l.id} className="border-t border-stone-100 hover:bg-stone-50">
                    <td className="px-4 py-3">
                      <button
                        onClick={() => copyLeadId(l.id)}
                        className="ltr text-xs bg-stone-100 hover:bg-stone-200 rounded px-2 py-0.5 font-mono flex items-center gap-1"
                        data-testid={`copy-lead-${l.id}`}
                      >
                        {l.id} <Copy className="w-3 h-3" />
                      </button>
                    </td>
                    <td className="px-4 py-3 text-xs text-stone-500">{formatDate(l.created_at)}</td>
                    <td className="px-4 py-3 text-xs">{l.utm_source || "—"}</td>
                    <td className="px-4 py-3 text-xs">
                      {l.first_offer_click_at
                        ? <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 rounded-full px-2 py-0.5 font-bold"><MousePointerClick className="w-3 h-3" />{formatDate(l.first_offer_click_at)}</span>
                        : <span className="text-stone-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {l.first_offer_click_link_key
                        ? <code className="ltr bg-amber-50 text-brand-amber rounded px-1.5 py-0.5 border border-amber-200">{l.first_offer_click_link_key}</code>
                        : <span className="text-stone-400">—</span>}
                    </td>
                    <td className="px-4 py-3 font-bold text-brand-amber ltr text-left">${l.revshare_earned || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="text-center text-[11px] text-stone-400 py-4">
          Dashboard מעודכן בזמן אמת · <a href="https://emetohovot.co.il" target="_blank" rel="noreferrer" className="underline">אמת או חובות <ExternalLink className="w-3 h-3 inline" /></a>
        </div>
      </main>
    </div>
  );
}

function Kpi({ label, value, hint, icon: Icon, accent, testId }) {
  const colors = {
    amber: "bg-amber-50 border-amber-200 text-brand-amber",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    stone: "bg-stone-50 border-stone-200 text-stone-500",
  };
  return (
    <div className={`rounded-2xl border p-4 ${colors[accent] || colors.stone}`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon className="w-4 h-4 opacity-70" />}
        <div className="text-[11px] font-bold uppercase tracking-wider opacity-80">{label}</div>
      </div>
      <div className="text-3xl font-[800] mt-1">{value ?? 0}</div>
      {hint && <div className="text-[11px] opacity-70 mt-1 leading-[1.4]">{hint}</div>}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("he-IL", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch (_) {
    return iso;
  }
}
